import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, useTheme } from "@mui/material";
import { FIELD_NUMBER_MAXVALUE, FIELD_NUMBER_MINVALUE } from "app/appConstants";
import { MprcConfirmDialog, MprcJoinComponents, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { IconButtonClose } from "components/iconButtons";
import { IconButtonExpandScreen } from "components/iconButtons/IconButtonExpandScreen";
import { ActionPlayerSelectQuestion, AssessmentAlternativeDto, AssessmentAlternativeSaveDto, AssessmentChallengeRevisionDto, AssessmentInstanceDto, AssessmentInstanceResultDto, AssessmentMode, AssessmentPlayerMode, AssessmentQuestionDto, AssessmentSaveResultDto, ConfidenceLevel, DeployApplicationType, QuestionClass, QuestionType, SurveyReviewBehaviorBeforeExpire } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { useReferredState } from "hooks/useReferredState";
import { Formaters } from "lib/Formaters";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ALTERNATIVE_FALSE, ALTERNATIVE_TRUE } from "views/assessment/common/assessment";
import { AssPlayerConclusion } from "views/assessment/player/Conclusion/AssPlayerConclusion";
import { AssPlayerConclusionActions } from "views/assessment/player/Conclusion/AssPlayerConclusionActions";
import { AssPlayerQuestion } from "views/assessment/player/Question/AssPlayerQuestion";
import { AssPlayerQuestionActions } from "views/assessment/player/Question/AssPlayerQuestionActions";
import { AssPlayerStart } from "views/assessment/player/Start/AssPlayerStart";
import { AssPlayerStartActions } from "views/assessment/player/Start/AssPlayerStartActions";

export type AssPlayerMode = "play" | "finalResult" | "preview";

/////////////////////////////////////////////////////////////////////////////////

type ExecuteStepOptions = "start" | "question" | "conclusion";

type InstanceContextType = {
  selectedAlternative?: AssessmentAlternativeDto | null;
  fullScreenMode: boolean;
  deployId?: string;
  question?: AssessmentQuestionDto;
  result?: AssessmentInstanceResultDto;
  viewData?: AssessmentInstanceDto;
  challengeRev: AssessmentChallengeRevisionDto | undefined;
  setChallengeRev: React.Dispatch<React.SetStateAction<AssessmentChallengeRevisionDto | undefined>>;
  changeSelectedAlternative: (alternativeId?: AssessmentAlternativeDto | null) => void;
  saveQuestion: (confidenceLevel?: ConfidenceLevel) => void;
  createNewInstance: () => void;
};

const InstanceContext = React.createContext<InstanceContextType>(undefined!);

////////////////////////////////////////////////////////////////////////////////

export const useInstanceContext = () => React.useContext(InstanceContext);

/*
    Pesquisa
    Modo Preview => Sempre será criada uma instância com Try-number = -1, sem referência.
    deployId        => Quando não for mode revisão, sempre deve existir o deployId para pegar o contexto para apresentar o resultado.
    participantId   => Será obrigatório para pesquisas do tipo enrollment conforme o tipo e quando for nulo para identificar que precisa criar um participante para pesquisa adhoc
    startMode?:     => Para identificar se é para iniciar em modo de revisão.
*/

interface IPros {
  instanceId?: string | null;
  deployId?: string;
  participantId?: string;
  startPlayerMode?: AssessmentPlayerMode;
  onClose: (success: boolean) => void;
  createInstance: () => Promise<AssessmentInstanceDto>;
  startInstance: (instanceId: string) => Promise<AssessmentSaveResultDto>;
  getStartPlayer: (playerMode?: AssessmentPlayerMode, instanceId?: string | null) => Promise<AssessmentInstanceDto>;
  getQuestion: (action: ActionPlayerSelectQuestion, playerMode: AssessmentPlayerMode, instanceId?: string | null, questionIdRef?: string | null, versionRef?: number | null) => Promise<AssessmentQuestionDto>;
}

/////////////////////////////////////////////////////////////////////////////////

export const AssPlayerBase: React.FC<IPros> = (props) => {
  const { createInstance, startInstance, getStartPlayer, getQuestion, deployId, startPlayerMode, onClose } = props;

  const api = useCoreApi();
  const { t } = useTranslation("assessment");
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [dialog, setDialog] = useState<any>();
  const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [reLoading, setReloading] = useState<boolean>(false);
  const [loadingQuestion, setLoadingQuestion] = useState<boolean>(false);
  const [savingQuestion, setSavingQuestion, refSavingQuestion] = useReferredState<boolean>(false);
  const [viewError, setViewError] = useState<any>();
  const [step, setStep] = useState<ExecuteStepOptions>("start");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [essayText, setEssayText] = useState<string>();
  const [rating, setRating] = useState<number>(0);
  const [lostConnection, setLostConnection] = useState<boolean>(false);
  const [posting, setPosting] = useState<boolean>(false);
  const [question, setQuestion] = useState<AssessmentQuestionDto>();
  const [result, setResult] = useState<AssessmentInstanceResultDto>();
  const viewData = useRef<AssessmentInstanceDto>(undefined);
  const questionSequence = useRef<number>(0);
  const [selectedAlternative, setSelectedAlternative] = useState<AssessmentAlternativeDto | null>();
  const [challengeRev, setChallengeRev] = useState<AssessmentChallengeRevisionDto>();

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [chronometer, setChronometer] = useState<boolean>(false);
  const [displayTime, setDisplayTime] = useState<number>(0);
  const [progress, setProgress] = useState<number>(0);
  const timeToComplete = useRef<number>(0);
  const elapsedTime = useRef<number>(0);
  const timerCount = useRef<number>(0);
  const leftTime = timeToComplete.current - elapsedTime.current;

  const isReview = viewData.current && viewData.current.endTime != null
    && (viewData.current.playerMode == AssessmentPlayerMode.UserReview || viewData.current.playerMode == AssessmentPlayerMode.FinalResult);
  const preview = viewData.current?.tryNumber == -1;

  const isValid = (): boolean => {
    if (question?.required == false) return true;

    switch (question?.questionType) {
      case QuestionType.SimpleChoice:
      case QuestionType.MultipleChoice:
        return selectedItems.length > 0;
      case QuestionType.Essay:
        return (essayText != undefined && essayText.length > 0);
      case QuestionType.Star:
        return rating > 0;
      case QuestionType.FillInNumber:
        var answers = question.answers?.map(x => !isNaN(Number(x.answer)) ? Number(x.answer) : 0);
        const minValue = question.questionClass == QuestionClass.Evaluation ? FIELD_NUMBER_MINVALUE : question.fillInMinValue;
        const maxValue = question.questionClass == QuestionClass.Evaluation ? FIELD_NUMBER_MAXVALUE : question.fillInMaxValue;
        return question?.answers != null
          && answers
          && question.answers.length == question.alternatives.length
          && !question.answers.some(x => x.answer == null || x.answer.length == 0)
          && !answers.some(x => x < minValue || x > maxValue);
      default:
        return question?.answers != null
          && question.answers.length == question.alternatives.length
          && !question.answers.some(x => x.answer == null || x.answer.length == 0);
    }
  };

  const canNext = isValid() || viewData.current?.playerMode == AssessmentPlayerMode.UserReview || viewData.current?.playerMode == AssessmentPlayerMode.FinalResult;

  ///
  const saveQuestion = (confidenceLevel?: ConfidenceLevel) => {
    setSavingQuestion(true);
    var alternatives = question!.alternatives.map((x) => ({
      alternativeId: x.alternativeId,
      answer: question?.questionType == QuestionType.Essay
        ? essayText
        : question?.questionType == QuestionType.Star
          ? x.text == rating.toString() ? ALTERNATIVE_TRUE : ALTERNATIVE_FALSE
          : question?.questionType == QuestionType.SimpleChoice || question?.questionType == QuestionType.MultipleChoice
            ? selectedItems.some(a => a == x.alternativeId) ? ALTERNATIVE_TRUE : ALTERNATIVE_FALSE
            : question?.answers?.find(a => a.alternativeId == x.alternativeId)?.answer ?? ""
    } as AssessmentAlternativeSaveDto)) ?? [];

    if (viewData.current!.instanceId) {
      api.v1.assPlayer.saveAnswerAssInstance({
        instanceId: viewData.current!.instanceId,
        questionId: question!.questionId,
        version: question!.version,
        alternatives: alternatives,
        confidenceLevel: confidenceLevel ?? null,
        elapsedTime: elapsedTime.current
      }).then(result => {
        if (result.data.questionResult) {
          defineQuestion(result.data.questionResult, result.data.questionResult.displayOrder);
          if (viewData.current && questionSequence.current == viewData.current.totalQuestions) {
            defineFinish();
          }
        }
        else if (result.data.nextQuestion != null) {
          defineQuestion(result.data.nextQuestion, result.data.nextQuestion.displayOrder);
        }
        else if (result.data.instanceResult != null) {
          setResult(result.data.instanceResult);
          setStep("conclusion");
          setChronometer(false);
        }
      }).catch(e => {
        if (isOffline(e)) {
          setLostConnection(true);
        } else {
          setViewError(e);
        }
      }).finally(() => setSavingQuestion(false));
    } else {
      throw new Error("Save operation allowed only with a instance.");
    }
  };

  ///
  const closeDialog = (success: boolean) => {
    if (isReview) {
      onClose(success);
    } else if (preview && viewData.current!.instanceId) {
      setPosting(true);
      api.v1.assPlayer.deletePreviewAssInstance({ instanceId: viewData.current!.instanceId })
        .then(result => onClose(success))
        .catch(e => setViewError);
    } else if (step == "question"
      && viewData.current!.mode == AssessmentMode.Evaluation
      && deployId
      && (viewData.current?.playerMode == AssessmentPlayerMode.NewInstance || viewData.current?.playerMode == AssessmentPlayerMode.Resume)) {
      setDialog(
        <MprcConfirmDialog
          open
          message={t("AssPlayer.Common.CloseEvalEficacyConfirm")}
          onClose={(confirm) => { if (confirm) onClose(false); else setDialog(null); }}
        />
      );
    } else if (step == "question" && viewData.current!.eval && viewData.current!.eval.allowContinueAborted == false) {
      const isLastTry = viewData.current!.eval?.maxTries <= viewData.current!.tryNumber;
      setDialog(
        <MprcConfirmDialog
          open={true}
          message={t(isLastTry ? "AssPlayer.Common.CloseLastTryConfirm" : "AssPlayer.Common.CloseLostTryConfirme")}
          onClose={(confirm) => { if (confirm) onClose(false); else setDialog(null); }}
        />
      );
    } else if (step == "question"
      && viewData.current!.mode == AssessmentMode.Survey
      && viewData.current?.playerMode != AssessmentPlayerMode.FinalResult
      && viewData.current?.playerMode != AssessmentPlayerMode.UserReview) {
      setDialog(
        <MprcConfirmDialog
          open={true}
          message={t("AssPlayer.Common.CloseSurveyConfirm")}
          onClose={(confirm) => { if (confirm) onClose(false); else setDialog(null); }}
        />
      );
    } else {
      onClose(success);
    }
  };

  /// Atualiza o estado para o display.
  function displayLeftTime() {
    setDisplayTime(timeToComplete.current - elapsedTime.current);
  }

  /// Ativa o cronômetro para registrar o tempo de resposta da avaliação/pesquisa.
  /// Define o tempo de execução.
  function defineExecutionTime(instanceDto: AssessmentInstanceDto) {
    // Se parou de responder, obtem o último tempo registrado.
    // Se é início da execução, instanceDto.elapsedTime já vem definido como 0;
    elapsedTime.current = instanceDto.elapsedTime ?? 0;

    // Se for uma avaliação e tiver um tempo máximo para preencher.
    // Atualiza o tempo para completar
    if (instanceDto.eval?.timeToComplete != null) {
      timeToComplete.current = instanceDto.eval.timeToComplete;
      displayLeftTime();
    }

    // Controle de tempo de resposta só é ativado se a instância estiver em mode de edição.
    if (instanceDto.playerMode == AssessmentPlayerMode.NewInstance
      || instanceDto.playerMode == AssessmentPlayerMode.Resume
      || instanceDto.playerMode == AssessmentPlayerMode.Update
    ) {
      setChronometer(true);
    }
  }

  ///
  const startHandle = () => {
    if (viewData.current!.playerMode == AssessmentPlayerMode.UserReview
      || viewData.current!.playerMode == AssessmentPlayerMode.FinalResult
      || viewData.current!.playerMode == AssessmentPlayerMode.Update
    ) {
      getFirstQuestion();
      // Ativa o cronômetro para atualizar os contadores e acumular o tempo ao responder novamente.
      if (viewData.current!.playerMode == AssessmentPlayerMode.Update) {
        defineExecutionTime(viewData.current!);
      }
    }
    else {
      setPosting(true);
      if (viewData.current!.instanceId) {
        startInstance(viewData.current!.instanceId)
          .then(result => {
            defineExecutionTime(viewData.current!);
            if (result.nextQuestion != null) {
              defineQuestion(result.nextQuestion, result.nextQuestion.displayOrder);
              setStep("question");
            }
          })
          .catch(e => setViewError(e))
          .finally(() => setPosting(false));
      } else {
        throw new Error("Just a instance can be started.");
      }
    }
  };

  const defineFinish = () => {
    setProgress(100);
    displayLeftTime();
    setChronometer(false);
  };

  // Finaliza a avaliação quando o tempo acabou.
  const finishEndTime = () => {
    defineFinish();
    setPosting(true);
    if (viewData.current!.instanceId) {
      api.v1.assPlayer.finishAssInstance({ instanceId: viewData.current!.instanceId, elapsedTime: elapsedTime.current })
        .then(result => {
          if (result.data.success) {
            setStep("conclusion");
            setResult(result.data.instanceResult!);
          }
        })
        .catch(e => setViewError(e))
        .finally(() => setPosting(false));
    } else {
      throw new Error("Just a instance can be finished.");
    }
  };

  // Atualiza a questão.
  function defineQuestion(dto: AssessmentQuestionDto, sequence: number) {
    questionSequence.current = sequence;
    setQuestion(dto);
    if (dto.questionType == QuestionType.SimpleChoice || dto.questionType == QuestionType.MultipleChoice) {
      setSelectedItems(dto.answers == null || dto.answers.length == 0 ? [] : dto.answers.filter(x => x.answer == ALTERNATIVE_TRUE).map(x => x.alternativeId));
      setEssayText("");
      setRating(0);
    } else if (dto.questionType == QuestionType.Star) {
      var defaultRating = 0;
      const answerItem = dto.answers?.find(x => x.answer == ALTERNATIVE_TRUE);
      if (answerItem) {
        var alternative = dto.alternatives.find(x => x.alternativeId == answerItem.alternativeId);
        defaultRating = parseInt(alternative?.text ?? "0");
      }
      setRating(defaultRating);
      setSelectedItems([]);
      setEssayText("");
    } else {
      setSelectedItems([]);
      setEssayText(dto.answers && dto.answers.length > 0 ? dto.answers[0].answer : "");
      setRating(0);
    }
    setStep("question");
  }

  // Busca a primeira pergunta do player.
  const getFirstQuestion = () => {
    setLoadingQuestion(true);
    getQuestion(ActionPlayerSelectQuestion.Current, viewData.current!.playerMode, viewData.current!.instanceId, null, null)
      .then((result) => {
        defineQuestion(result, 1);
      })
      .catch(e => setViewError(e))
      .finally(() => setLoadingQuestion(false));
  };

  // Busca a próxima pergunta para apresentação.
  const getNextQuestion = (questionId: string, version?: number) => {
    setLoadingQuestion(true);
    getQuestion(ActionPlayerSelectQuestion.Next, viewData.current!.playerMode, viewData.current!.instanceId, questionId, version)
      .then((result) => {
        defineQuestion(result, viewData.current!.playerMode == AssessmentPlayerMode.FinalResult ? questionSequence.current + 1 : result.displayOrder);
      })
      .catch(e => setViewError(e))
      .finally(() => setLoadingQuestion(false));
  };

  // Busca a pergunta anterior para apresentação.
  const getBackQuestion = () => {
    setLoadingQuestion(true);
    getQuestion(ActionPlayerSelectQuestion.Previous, viewData.current!.playerMode, viewData.current!.instanceId, question?.questionId, question?.version)
      .then((result) => {
        defineQuestion(result, viewData.current!.playerMode == AssessmentPlayerMode.FinalResult ? questionSequence.current - 1 : result.displayOrder);
      })
      .catch(e => setViewError(e))
      .finally(() => setLoadingQuestion(false));
  };

  ///
  const goFinish = () => {
    if (result == null && viewData.current && viewData.current.mode == AssessmentMode.Evaluation && viewData.current.instanceId) {
      setLoadingQuestion(true);
      api.v1.assPlayer.getEvalResultInstance({ instanceId: viewData.current.instanceId })
        .then((result) => {
          setResult(result.data);
          setStep("conclusion");
        })
        .catch(e => setViewError(e))
        .finally(() => setLoadingQuestion(false));
    } else {
      setStep("conclusion");
    }
  };

  // Carrega a inicialiazação ou reload o player.
  async function loadPlayer(playerMode?: AssessmentPlayerMode) {

    const loadInstanceId = viewData.current ? viewData.current.instanceId : props.instanceId;

    try {
      setLoading(true);

      // Se o player foi executado em modo de pesquisa para revisão final.
      if (playerMode == AssessmentPlayerMode.FinalResult) {
        let result = await getStartPlayer(AssessmentPlayerMode.FinalResult, loadInstanceId);
        viewData.current = result;
        setStep("start");
      }
      // Se o player está sendo executado no modo que o usuário pode visualizar e alterar as respostas.
      else if (playerMode == AssessmentPlayerMode.Update) {
        let result = await getStartPlayer(AssessmentPlayerMode.Update, loadInstanceId);
        viewData.current = result;
        setStep("start");
      }
      // Se player foi executado por uma instância já existe.
      else if (loadInstanceId) {
        let result = await getStartPlayer(undefined, loadInstanceId);
        viewData.current = result;

        if (result.playerMode == AssessmentPlayerMode.Resume) {
          defineExecutionTime(result);
          if (result.lastQuestionAnsweredId == null) {
            getFirstQuestion();
          } else {
            getNextQuestion(result.lastQuestionAnsweredId);
          }
        }
        else if (result.playerMode == AssessmentPlayerMode.UserReview || result.playerMode == AssessmentPlayerMode.FinalResult) {
          setStep("start");
        } else {
          defineExecutionTime(result);
          if (result.endTime != null || result.lastQuestionAnsweredId == null) {
            getFirstQuestion();
          } else {
            getNextQuestion(result.lastQuestionAnsweredId);
          }
        }
      }
      // Se player foi executado para responder uma instância pela primeira vez.
      else {
        let result = await createInstance();
        viewData.current = result;
      }
    } catch (e) {
      setViewError(e);
    } finally {
      setLoading(false);
    }
  }

  function isOffline(e: any): boolean {
    return (e.response && (e.response.status && e.response.status == 404 || e.response.status == '404'))
      || e.dueToNoInternetConnection
      || (e?.message?.toLowerCase() == 'network error');

  }

  ///
  const createNewInstance = () => {
    setReloading(true);
    createInstance().then(result => {
      viewData.current = result;
      loadPlayer();
    })
      .catch(e => setViewError(e))
      .finally(() => setReloading(false));
  };

  ////////////////////////////////////////////////////////////////////////////
  /// EFFECT's
  ////////////////////////////////////////////////////////////////////////////

  // Ao carregar a player.
  useEffect(() => {
    loadPlayer(props.startPlayerMode);
  }, []);

  // Ao navegar pelas questões.
  useEffect(() => {
    setSelectedAlternative(null);
  }, [step, question]);

  // Controles de tempo
  useDidUpdateEffect(() => {
    var errorConnection = false;

    if (!isReview) {

      if (timer != null) clearInterval(timer.current!);

      if (chronometer) {
        timer.current = setInterval(() => {
          if (!errorConnection) {

            // Calcula o tempo decorrido somente se existe o tempo máximo para responder.
            // Válido somente para avaliação com tempo máximo definido.
            // Para os demais caso, sempre registrar o tempo de resposta para cálculos de média de tempo de resposta.
            if (viewData.current!.eval?.timeToComplete) {

              // Registra o tempo, se o tempo passou e tem tempo máximo definido, força a finalização.
              // O backend também controla o tempo máximo e finaliza automaticamente para casos específicos.
              if (timeToComplete.current - elapsedTime.current > 0) {
                elapsedTime.current += 1;
                displayLeftTime();
                if (elapsedTime.current % 2 == 0 && timeToComplete.current > 0) {
                  setProgress(elapsedTime.current / timeToComplete.current * 100);
                }
              } else if (viewData.current!.eval?.timeToComplete != null) {
                finishEndTime();
              }
            } else {
              elapsedTime.current += 1;
            }
          }

          timerCount.current += 1;
          if (timerCount.current % 10 == 0 && viewData.current!.instanceId && !refSavingQuestion) {
            api.v1.assPlayer.pingAssInstance({ instanceId: viewData.current!.instanceId, elapsedTime: elapsedTime.current })
              .then(result => {
                if (result.data) {
                  errorConnection = false;
                  setLostConnection(false);
                } else {
                  enqueueSnackbar(t("AssPlayer.Question.ErrorRegisterPingMessage"), { variant: "error" });
                }
              })
              .catch(e => {
                if (isOffline(e)) {
                  errorConnection = true;
                  setLostConnection(true);
                } else {
                  setViewError(e);
                }
              });
          }
        }, 1000);
      }

      return () => {
        if (timer != null) clearInterval(timer.current!);
      };
    }
  }, [chronometer]);

  ////////////////////////////////////////////////////////////////////////////

  ///
  const RenderTitle = () => {
    return (
      <Typography variant="h4" component="h1" color={theme.palette.muted.main} noWrap>
        <strong>
          {
            viewData.current!.playerMode == AssessmentPlayerMode.UserReview && viewData.current!.mode == AssessmentMode.Evaluation
              ? viewData.current!.title
              : viewData.current!.mode == AssessmentMode.Survey && viewData.current!.playerMode == AssessmentPlayerMode.FinalResult
                ? t("AssPlayer.Common.FinalResultTitle")
                : viewData.current!.mode == AssessmentMode.Challenge
                  ? t("AssPlayer.Common.TitleChallenge")
                  : step == "conclusion" && viewData.current!.mode == AssessmentMode.Evaluation
                    ? t("AssPlayer.Conclusion.TitleConclusion")
                    : viewData.current!.mode == AssessmentMode.Evaluation
                      ? t("AssPlayer.Evaluation.Title")
                      : t("AssPlayer.Survey.Title")
          }
        </strong>
      </Typography>
    );
  };

  ///
  const buildSubtitle = () => {
    var items: React.ReactNode[] = [];
    const surv = viewData.current!.surv!;

    if (viewData.current?.deploy) {
      items.push(
        <Typography variant={viewData.current!.endTime || step != "start" ? "caption" : "bodyNotice"} component="span" textTransform="none">
          {t("AssPlayer.StartBox.UserAboutSubtitle", { name: viewData.current.deploy.trainingName })}

          {viewData.current.deploy.applicationType == DeployApplicationType.LmsManagerEfficacy &&
            <>&nbsp;&ndash;&nbsp;{viewData.current.deploy.username}</>
          }
        </Typography>
      );
    }

    if (step == "start") {
      if (viewData.current!.endTime) {
        items.push(
          <Typography variant="caption" component="span" textTransform="none">
            {t("AssPlayer.Survey.TimeEndSubtitle", { name: Formaters.performaDate(viewData.current!.endTime, { dayOfWeek: true, preposition: "none" }) })}
          </Typography>
        );
        if (surv?.expired == false && surv.beforeExpireBehavior != SurveyReviewBehaviorBeforeExpire.Modify) {
          items.push(<Typography variant="caption" component="span" textTransform="none">{t("AssPlayer.Survey.CannotModifyText")}</Typography>);
        }
      }
      else if (viewData.current!.surv?.deadline) {
        items.push(
          <Typography variant="bodyNotice" component="span" textTransform="none">
            {(viewData.current?.survRevision?.endDate == null || viewData.current?.survRevision?.endDate > new Date())
              ? t(startPlayerMode == AssessmentPlayerMode.FinalResult ? "AssPlayer.Survey.DeadlineFinalResultText" : "AssPlayer.Survey.DeadlineText", { name: Formaters.performaDate(viewData.current!.surv.deadline, { dayOfWeek: true, preposition: "none" }) })
              : t("AssPlayer.Survey.FinalizationText", { date: Formaters.performaDate(viewData.current!.survRevision?.endDate, { dayOfWeek: false, preposition: "none" }) })
            }
          </Typography>
        );
      }
    }

    return <Typography variant="caption" component="p"><MprcJoinComponents components={items} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
  };

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  return (
    <>
      {dialog}

      <MprcRequestErrorDialog error={viewError} onClose={() => onClose(false)} />

      <MprcRequestIndicator type={"request"} visible={loading} />

      <InstanceContext.Provider
        value={{
          viewData: viewData.current,
          question: question,
          result: result,
          selectedAlternative,
          fullScreenMode,
          deployId: deployId,
          saveQuestion: saveQuestion,
          challengeRev: challengeRev,
          setChallengeRev,
          changeSelectedAlternative: (alternative) => setSelectedAlternative(alternative),
          createNewInstance
        }}
      >
        {
          viewData.current && (viewData.current.playerMode != AssessmentPlayerMode.Resume || question != undefined) &&
          <Dialog
            open
            fullWidth
            maxWidth="md"
            onClose={() => closeDialog(false)}
            fullScreen={fullScreenMode}
            sx={fullScreenMode ? { "& .MuiPaper-root": { maxHeight: "98%", maxWidth: "1024px" } } : undefined}
          >
            <MprcRequestIndicator
              type={savingQuestion || posting ? "post" : "request"}
              visible={loadingQuestion || lostConnection || posting || savingQuestion || reLoading}
            />

            <DialogTitle>
              <Grid container justifyContent="space-between" alignItems="center" width="100%" wrap="nowrap" spacing={0.5}>
                <Grid size="grow">
                  {RenderTitle()}
                  {buildSubtitle()}
                </Grid>
                <Grid>
                  <IconButtonExpandScreen
                    expanded={fullScreenMode}
                    size="small"
                    onClick={() => setFullScreenMode(!fullScreenMode)}
                  />
                </Grid>
                <Grid>
                  <IconButtonClose
                    color="inherit"
                    size="small"
                    onClick={() => closeDialog(false)}
                  />
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent sx={{ height: fullScreenMode ? "70vh" : "52vh", maxWidth: "100%" }}>
              {
                step == "start"
                  ?
                  <AssPlayerStart
                    fullScreenMode={fullScreenMode}
                    viewData={viewData.current}
                  />
                  : step == "question"
                    ?
                    <AssPlayerQuestion
                      deployId={deployId}
                      selectedItems={selectedItems}
                      essayText={essayText}
                      rating={rating}
                      lostConnection={lostConnection}
                      onChangeMultiChoice={(selectedItems) => setSelectedItems(selectedItems)}
                      onChangeQuestion={(question) => setQuestion(question)}
                      onChangeEssay={(essay) => setEssayText(essay)}
                      onChangeStar={(rating) => setRating(rating)}
                    />
                    :
                    <AssPlayerConclusion
                      fullScreenMode={fullScreenMode}
                      viewData={viewData.current}
                      result={result!}
                    />
              }
            </DialogContent>
            <DialogActions>
              {
                step == "start"
                  ?
                  <AssPlayerStartActions
                    loading={loading}
                    viewData={viewData.current}
                    onStartClick={startHandle}
                  />
                  : step == "question"
                    ?
                    <AssPlayerQuestionActions
                      viewData={viewData.current!}
                      question={question!}
                      leftTime={leftTime}
                      displayTime={displayTime}
                      progress={progress}
                      sequence={questionSequence.current}
                      canNext={canNext}
                      goNextQuestion={() => getNextQuestion(question?.questionId!, question?.version)}
                      goBackQuestion={() => {
                        if (questionSequence.current == 1)
                          setStep("start");
                        else
                          getBackQuestion();
                      }}
                      saveQuestion={() => saveQuestion()}
                      onClose={(success) => closeDialog(success)}
                      goFinish={() => goFinish()}
                    />
                    : <AssPlayerConclusionActions
                      preview={preview}
                      onClose={() => closeDialog(true)}
                      onReview={() => loadPlayer(viewData.current!.mode == AssessmentMode.Survey ? AssessmentPlayerMode.FinalResult : undefined)}
                      goBackQuestion={() => setStep("question")}
                    />
              }
            </DialogActions>
          </Dialog>
        }
      </InstanceContext.Provider>
    </>
  );
};
